<template>
    <ul class="lender-stats">
        <li
            v-for="(stat, index) in statsConfig"
            :key="index"
        >
            <span>{{ stat.label }}:</span>
            <span>
                <span v-html="stat.value" />
                <Tooltip 
                    v-if="stat.showTooltip"
                    :data="stat.tooltipData"
                />
            </span>
        </li>
    </ul>
</template>

<script>
import { capitalize } from 'lodash';
import Tooltip from '@/components/features/Tooltip.vue';
import PayoutTime from '@/mixins/PayoutTime';

export default {
    components: {
        Tooltip
    },
    mixins: [PayoutTime],
    props: {
        model: {
            type: Object,
            required: true
        },
        statsType: {
            type: String,
            requried: true,
            default: 'private'
        }
    },
    computed: {
        statsConfig() {
            let stats = [
                {
                    label: this.$translate('amount', 'Belopp'),
                    value: this.model.amountString,
                    showTooltip: this.model.hasDebtCollectionAmount,
                    tooltipData: this.$translate('debt_collection_amount_tooltip', 'Vid ett samlingslån gäller istället {amount}')
                        .replace('{amount}', this.model.amountDebtCollectionString)
                },
                {
                    label: this.$translate('interest', 'Ränta'),
                    value: this.model.interestString
                },
                {
                    label: this.$translate('effective_interest', 'Effektiv ränta'),
                    value: this.model.effectiveInterestString
                },
                {
                    label: this.$translate('duration', 'Löptid'),
                    value: this.model.getDurationString(this.$translate('month_short', 'mån'), this.$translate('year', 'år')),
                    showTooltip: this.model.hasDebtCollectionDuration,
                    tooltipData: this.$translate('debt_collection_duration_tooltip', 'Vid ett samlingslån gäller istället {duration}')
                        .replace('{duration}', this.model.getDurationDebtCollectionString(this.$translate('month_short', 'mån'), this.$translate('year', 'år')))
                },
                {
                    label: this.$translate('ld_age_limit_label', 'Ålderskrav'),
                    value: this.$translate('ld_age_limit_value', '{minAge} eller äldre')
                        .replace('{minAge}', this.model.minAge)
                },
            ];

            if (this.model.isBroker) {
                stats.push({
                    label: this.model.isRealBroker ? 
                        this.$translate('connected_banks', 'Anslutna långivare') : 
                        this.$translate('connected_banks_affiliate_broker', 'Jämförda långivare'),
                    value: this.model.connectedBanks
                });
            }

            stats.push({
                label: this.$translate('ld_payment_remarks_label', 'Betalningsanmärkningar'),
                value: this.model.acceptsPaymentRemarks ? this.$translate('accepted', 'Accepteras') : this.$translate('not_accepted', 'Accepteras inte')
            });

            if (this.$isSweden()) {

                if (this.model.isAffiliateBroker) {
                    stats = stats.concat([{
                        label: this.$translate('without_uc', 'Utan UC'),
                        value: (!this.model.takesUC) ? this.$translate('yes', 'Ja') : this.$translate('no', 'Nej')
                    }]);
                }
                else {
                    stats = stats.concat([
                        {
                            label: this.$translate('without_uc', 'Utan UC'),
                            value: (!this.model.takesUC) ? this.$translate('uses_credit_check', 'Ja, använder {creditCheck}')
                                .replace('{creditCheck}', capitalize(this.model.creditCheck)) : this.$translate('no', 'Nej')
                        }
                    ]);
                }   

                stats = stats.concat([
                    {
                        label: this.$translate('apply_with_bankid', 'Ansök med BankID'),
                        value: this.$helpers.boolean(this.model.hasBankId)
                    },
                    {
                        label: this.$translate('loan_protection_can_be_signed', 'Låneskydd kan tecknas'),
                        value: this.$helpers.boolean(this.model.hasLoanProtection)
                    },
                    {
                        label: this.$translate('direct_payout', 'Direkt­utbetalning'),
                        value: this.model.hasDirectPayments 
                            ? this.payoutDateDayTimeString 
                            : this.$translate('no', 'Nej') 
                    },
                ]);
            }

            stats.push({
                label: this.$translate('rating', 'Betyg'),
                value: this.$translate('average_rating_score', '{averageRating} av 5 ({amount} omdömen)')
                    .replace('{averageRating}', this.$format.number(this.model._ratings.average_score, 1))
                    .replace('{amount}', this.model._ratings.count)
            });

            return stats;
        }
    },
};
</script>

<style lang="scss">
.lender-stats {
    li {
        @include grid(2, 20px);
        width: 100%;
        padding: 10px;
        font-family: $fontText;
        box-sizing: border-box;
        font-size: 14px;

        &:nth-child(even) { background: #fafafa; }

        i { margin-right: 10px; }

        span, div {
            &:first-child { text-align: right; }
            &:last-child { font-weight: bold; }
        }
    }
}
</style>
